import {InlineDatumReactComponentProps} from '@stripe-internal/inline-data';
import {useEffect, useRef, useState} from 'react';
import {SignInCard} from './SignInCard';
import {LinkSignInCard} from './LinkSignInCard';
import {TypingIndicator} from './TypingIndicator';
import {SignedInAsLabel} from './SignedInAsLabel';
import {CantSignInLabel} from './CantSignInLabel';

export type ValueProps = {
  isSignedIn: boolean;
  url: string;
  merchantName?: string;
  audience: string;
  linkBrandName?: string;
};

export const UniversalHelpSignInPrompt = (
  props: InlineDatumReactComponentProps,
) => {
  const {datum, fetch, onDone, isDone} = props;
  const value = datum.value as ValueProps;

  const isSignedIn = value?.isSignedIn || false;
  const url = value?.url || '';
  const merchantName = value?.merchantName;
  const audience = value?.audience || '';
  const linkBrandName = value?.linkBrandName;

  const [cantSignInSelected, setCantSignInSelected] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [displayName, setDisplayName] = useState<string | undefined>(
    merchantName,
  );
  const fetchInitialState = useRef<boolean>(true);
  const fetchLatestState = useRef<boolean>(true);

  useEffect(() => {
    if (merchantName) {
      setDisplayName(merchantName);
    }
  }, [merchantName]);

  useEffect(() => {
    if (isDone) {
      if (isSignedIn) {
        return;
      }

      if (fetchLatestState.current) {
        fetch({update: true});
        fetchLatestState.current = false;
      }
      return;
    }

    if (fetchInitialState.current) {
      fetch({update: true});

      fetchInitialState.current = false;
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  }, [isDone, isSignedIn, fetch]);

  useEffect(() => {
    if (isDone) {
      return;
    }

    if (isSignedIn || cantSignInSelected) {
      fetchLatestState.current = false;
      setIsLoading(false);
      const outcome = isSignedIn ? 'signed-in' : 'cant-sign-in';
      onDone({
        type: 'success',
        message: outcome,
        data: {
          outcome,
        },
      });
    }
  }, [isDone, isSignedIn, cantSignInSelected, onDone]);

  if (isDone || cantSignInSelected || displayName) {
    if (displayName) {
      return <SignedInAsLabel merchantName={displayName} />;
    } else {
      return <CantSignInLabel />;
    }
  }

  if (isLoading) {
    return <TypingIndicator />;
  }
  const isLinkAudience = audience === 'link';

  return isLinkAudience ? (
    <LinkSignInCard url={url} linkBrandName={linkBrandName} />
  ) : (
    <SignInCard url={url} setCantSignInSelected={setCantSignInSelected} />
  );
};
